import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { WithAuth } from "../../components/with-auth";
import ChatInterface from "../../components/chat-interfact";
import MainWrapper from "../../layout/MainWrapper";
import { createAxiosInstance } from "../../api/ApiRequest";
import type { GetProp, TableProps } from 'antd';
import { Button, Input, Table, Modal, Form, InputNumber, Row, Col, Select, Empty, Spin } from 'antd';
import type { SorterResult } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import { format } from 'date-fns';
import { SearchOutlined, EditOutlined, SendOutlined, UsergroupAddOutlined } from "@ant-design/icons";
import { useServerContext } from "../../context/ServerContext";
import { useAuth } from "../../context/AuthContext";
import { isMainBot } from "../../utils/serverTypeLabels";
import { notifyError, notifySuccess } from "../../utils/toast";

type ColumnsType<T extends object = object> = TableProps<T>['columns'];
type TablePaginationConfig = Exclude<GetProp<TableProps, 'pagination'>, boolean>;

interface DataType {
	_id: String,
	__v: String,
	index: Number,
    chat_id:  Number,
	username: String,
	firstname: String,
	lastname: String,
    wallet: String,
    withdraw_wallet:  String,
    active_token:  String,
    location:  String,
    referral_chat_id:  Number,
    expire_time: String ,
	referral_username: String,
	referral_wallet_address: String,
    createdAt:  Number,
}

interface TableParams {
	pagination?: TablePaginationConfig;
	sortField?: SorterResult<any>['field'];
	sortOrder?: SorterResult<any>['order'];
}

const getParams = (params: TableParams) => ({
	results: params.pagination?.pageSize,
	page: params.pagination?.current,
	...params,
});

interface Message {
    id: number;
    text: string;
    sender: "user" | "admin";
    timestamp: number;
}

interface UserItem {
	value: string;
	label: string;
}

const Users: React.FC = () => {
    const history = useNavigate();
    const { logout } = useAuth();
    const axiosInstance = createAxiosInstance(history, logout);
    const { selectedServer, loading: configLoading } = useServerContext();
    const isMainBotServer = selectedServer ? isMainBot(selectedServer.type) : false;

	const [search, setSearch] = useState('');
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [refreshFlag, setRefreshFlag] = useState(false);
    const [editId, setEditId] = useState(0);
    const [chatModalOpen, setChatModalOpen] = useState(false);
    const [userMessages, setUserMessages] = useState<Message[]>([]);
	const [referralChatIdModalOpen, setReferralChatIdModalOpen] = useState(false);
    const [userItems, setUserItems] = useState<UserItem[]>([]);

	const showModal = () => {
		setIsModalOpen(true);
	};

	const handleOk = () => {
		setIsModalOpen(false);
	};

	const handleCancel = () => {
		setIsModalOpen(false);
	}

    const onFinish = async (values: any) => {
        let loginRes = await axiosInstance.post(`/api/v1/update_server_expire_time`, {
            serverId: selectedServer?.serverId,
            userId: values.db_id,
            additional_time: values.additional_time,
        });
        if (loginRes.data) {
            let { flag, message } = loginRes.data;
			handleOk();
            if (!flag) {
                notifyError(message);
            } else {
				notifySuccess('Update Expire Time successed.');
				setRefreshFlag(!refreshFlag);
			}
        }
    };

    const onFinishFailed = (errorInfo: any) => {
        console.log('Failed:', errorInfo);
    };

    const onSetReferralFinish = async (values: any) => {
        let updateRes = await axiosInstance.post(`/api/v1/update_server_referral`, {
            ...values,
            serverId: selectedServer?.serverId,
        });
        if (updateRes.data) {
            let { flag, message } = updateRes.data;
			setReferralChatIdModalOpen(false)
            if (!flag) {
                notifyError("Network internal Error.");
            } else {
				notifySuccess('Update Referral Chat Id successed.');
				setRefreshFlag(!refreshFlag);
			}
        }
    };

	const [form] = Form.useForm();

	const expireTimeEdit = (_id: String) => {
		showModal();
		form.setFieldsValue({
			db_id: _id,
			additional_time: undefined,
		});
	};

	const [referralForm] = Form.useForm();

	const highlight = (text: unknown) => {
		const value = text?.toString() ?? '';
		if (!search.trim()) return value;
		return (
			<Highlighter
				highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
				searchWords={[search.trim()]}
				autoEscape
				textToHighlight={value}
			/>
		);
	};

	const volumeMainUserColumns: ColumnsType<DataType> = isMainBotServer ? [
		{
			title: 'Wallet Address',
			dataIndex: 'wallet',
			width: '15%',
			sorter: false,
			render: highlight,
		},
		{
			title: 'Withdraw Wallet',
			dataIndex: 'withdraw_wallet',
			width: '15%',
			sorter: true,
			render: highlight,
		},
		{
			title: 'Expire Time',
			dataIndex: 'expire_time',
			width: '10%',
			sorter: true,
			render: (item: string) => {
				if (item && (item.split("||")).length > 1) return (
					<>
						{format((item.split("||"))[0], 'yyyy-MM-dd HH:mm')} <a href="#" title="give access" onClick={() => expireTimeEdit((item.split("||"))[1])}><EditOutlined /></a>
					</>
				);
				return (
					<a href="#" title="give access" onClick={() => expireTimeEdit((item.split("||"))[0])}><EditOutlined /></a>
				);
			},
		},
	] : [];

	const wlBotUserColumns: ColumnsType<DataType> = !isMainBotServer ? [
		{
			title: 'Referral Wallet',
			dataIndex: 'referral_wallet_address',
			width: '15%',
			sorter: false,
			render: highlight,
		},
	] : [];

	const columns: ColumnsType<DataType> = [
		{
			title: 'No',
			dataIndex: 'index',
			sorter: true,
			width: '5%',
			render: highlight,
		},
		{
			title: 'User Name',
			dataIndex: 'username',
			width: '10%',
			sorter: true,
			render: (item) => {
				const username = item?.toString() ?? '';
				return (
					<a href={"https://t.me/" + username.replace('@', '')} target="_blanks" title="Go to telegram">
						{highlight(username)}
					</a>
				);
			},
		},
		{
			title: 'First Name',
			dataIndex: 'firstname',
			sorter: true,
			width: '10%',
			render: highlight,
		},
		{
			title: 'Last Name',
			dataIndex: 'lastname',
			sorter: true,
			width: '10%',
			render: highlight,
		},
		...volumeMainUserColumns,
		...wlBotUserColumns,
		{
			title: 'Referral User Name',
			dataIndex: 'referral_username',
			width: '8%',
			sorter: true,
			render: (item) => {
				const username = item?.toString() ?? '';
				if (!username) return highlight(username);
				return (
					<a href={"https://t.me/" + username.replace('@', '')} target="_blanks" title="Go to telegram">
						{highlight(username)}
					</a>
				);
			},
		},
		{
			title: '',
			dataIndex: 'chat_id',
			width: '7%',
			sorter: false,
			render: (item) => (
				<>
					<a href="#" title="Set Referral Chat Id" onClick={()=>getUserList(item)}><UsergroupAddOutlined /></a>&nbsp;&nbsp;&nbsp;
					<a href="#" title="Send message" onClick={()=>getMessages(item)}><SendOutlined /></a>
				</>
			),
		},
	];

    const getMessages = async (id: number) => {
		setEditId(id);
        setLoading(true);
        let results = await axiosInstance.post('/api/v1/get_user_faq', { chatid: id });
        if (results.data && results.data.flag && results.data.flag == true) {
            setUserMessages(results.data.messages);
            setLoading(false);
            setChatModalOpen(true);
        } else {
            notifyError(results.data.message);
            setLoading(false);
        }
    }

    const getUserList = async (id: number) => {
		setEditId(id);
		referralForm.setFieldsValue({
			chat_id: id,
		});
        setLoading(true);
        let results = await axiosInstance.post('/api/v1/get_server_user_list', {
            serverId: selectedServer?.serverId,
        });
        if (results.data && results.data.flag && results.data.flag == true) {
            setUserItems(results.data.items);
            setLoading(false);
            setReferralChatIdModalOpen(true);
        } else {
            notifyError(results.data.message);
            setLoading(false);
        }
    }
		
	const [data, setData] = useState<DataType[]>();
	const [loading, setLoading] = useState(false);
	const [tableParams, setTableParams] = useState<TableParams>({
		pagination: {
			current: 1,
			pageSize: 10,
		},
	});

	const fetchData = async () => {
		if (!selectedServer) return;
		setLoading(true);
		let results = await axiosInstance.post('/api/v1/get_server_users', {
			serverId: selectedServer.serverId,
			search: search.trim() || undefined,
			...getParams(tableParams),
		});
		if (results.data && results.data.flag && results.data.flag == true) {
			setData(results.data.users);
			setLoading(false);
			setTableParams({
				...tableParams,
				pagination: {
					...tableParams.pagination,
					total: results.data.totalNum,
				},
			});
		} else {
			notifyError(results.data.message);
			logout();
		}
	};

	useEffect(() => {
		if (selectedServer) fetchData();
	}, [
		selectedServer?.serverId,
		tableParams.pagination?.current,
		tableParams.pagination?.pageSize,
		tableParams?.sortOrder,
		tableParams?.sortField,
		search,
		refreshFlag
	]);

	const handleTableChange: TableProps<DataType>['onChange'] = (pagination, _filters, sorter) => {
		setTableParams({
			pagination,
			sortOrder: Array.isArray(sorter) ? undefined : sorter.order,
			sortField: Array.isArray(sorter) ? undefined : sorter.field,
		});

		if (pagination.pageSize !== tableParams.pagination?.pageSize) {
			setData([]);
		}
	};


    return (
        <WithAuth>
            <MainWrapper>
                {configLoading ? (
                    <Spin />
                ) : !selectedServer ? (
                    <Empty description="No server selected" />
                ) : (
                <>
				<Input
					placeholder={
						isMainBotServer
							? 'Search username, name, withdraw wallet...'
							: 'Search username, name, referral wallet...'
					}
					prefix={<SearchOutlined />}
					allowClear
					value={search}
					onChange={(e) => {
						setSearch(e.target.value);
						setTableParams((prev) => ({
							...prev,
							pagination: { ...prev.pagination, current: 1 },
						}));
					}}
					style={{ marginBottom: 16, maxWidth: 480 }}
				/>
				<Table
					className="dashboard-data-table"
					columns={columns}
					rowKey={(record) => record._id.toString()}
					dataSource={data}
					pagination={tableParams.pagination}
					loading={loading}
					onChange={handleTableChange}
					bordered
					size="middle"
					scroll={{ x: 'max-content' }}
				/>
				<Modal title="Set Additional Expire Time" open={isModalOpen} footer={null} onOk={handleOk} onCancel={handleCancel}>
					<Form
						form={form}
						name="basic"
						labelCol={{
							span: 8,
						}}
						wrapperCol={{
							span: 16,
						}}
						style={{
							maxWidth: 600,
							margin: 'auto'
						}}
						initialValues={{
							remember: true,
						}}
						onFinish={onFinish}
						onFinishFailed={onFinishFailed}
						autoComplete="off"
					>
						<Form.Item
							label="Additional Hours"
							name="additional_time"
							rules={[
								{
									required: true,
									message: 'Please input additional hours!',
								},
								{
									type: 'number',
									min: 1,
									message: 'Hours must be at least 1',
								},
							]}
						>
							<InputNumber min={1} placeholder="Hours to add" style={{ width: '100%' }} />
						</Form.Item>

						<Form.Item
							label="D _id"
							name="db_id"
							style={{display: "none"}}
						>
							<Input />
						</Form.Item>
                
						<Row>
							<Col span={12}></Col>
							<Col span={6}>
								<Form.Item
									wrapperCol={{
										offset: 16,
										span: 2,
									}}
								>
									<Button type="primary" htmlType="submit">
										Ok
									</Button>
								</Form.Item>
							</Col>
							<Col span={6}>
								<Form.Item
									wrapperCol={{
										offset: 8,
										span: 2,
									}}
								>
									<Button type="default" onClick={handleCancel}>
										Cancel
									</Button>
								</Form.Item>
							</Col>
						</Row>
					</Form>
				</Modal>
				<Modal title="Set Referral Chat Id" open={referralChatIdModalOpen} footer={null} onOk={() => setReferralChatIdModalOpen(false)} onCancel={() => setReferralChatIdModalOpen(false)}>
					<Form
						form={referralForm}
						name="basic"
						labelCol={{
							span: 8,
						}}
						wrapperCol={{
							span: 16,
						}}
						style={{
							maxWidth: 600,
							margin: 'auto'
						}}
						initialValues={{
							remember: true,
						}}
						onFinish={onSetReferralFinish}
						onFinishFailed={onFinishFailed}
						autoComplete="off"
					>
						<Form.Item
							label="Referral Chat Id"
							name="referral_chat_id"
						>
							<Select
								showSearch
								style={{ width: 200 }}
								placeholder="Search to Select"
								optionFilterProp="label"
								filterSort={(optionA, optionB) =>
									(optionA?.label ?? '').toLowerCase().localeCompare((optionB?.label ?? '').toLowerCase())
								}
								options={userItems}
							/>
						</Form.Item>

						<Form.Item
							label="DB _id"
							name="chat_id"
							style={{display: "none"}}
						>
							<Input />
						</Form.Item>
                
						<Row>
							<Col span={12}></Col>
							<Col span={6}>
								<Form.Item
									wrapperCol={{
										offset: 16,
										span: 2,
									}}
								>
									<Button type="primary" htmlType="submit">
										Ok
									</Button>
								</Form.Item>
							</Col>
							<Col span={6}>
								<Form.Item
									wrapperCol={{
										offset: 8,
										span: 2,
									}}
								>
									<Button type="default" onClick={()=>setReferralChatIdModalOpen(false)}>
										Cancel
									</Button>
								</Form.Item>
							</Col>
						</Row>
					</Form>
				</Modal>
				<Modal
					title="Chat History"
					centered
					open={chatModalOpen}
					onOk={() => setChatModalOpen(false)}
					onCancel={() => setChatModalOpen(false)}
					width={1500}
				>
					<ChatInterface key={JSON.stringify(userMessages)} newMessages={userMessages} userChatId={editId} />
				</Modal>
                </>
                )}
            </MainWrapper>
        </WithAuth>
    )
}

export default Users;
